// Read-only 3D view of the warehouse layout.
//
// Same model as the plan view — this adds nothing routing depends on. Walking
// happens in 2D; heights only affect how long a pick takes, not how far the
// picker goes. What the 3D view is genuinely good for is CHECKING the drawing:
// a bay width or a shelf count that is wrong reads as an obviously wrong-shaped
// rack, in a way a column of numbers never does.
//
// three.js is loaded with `await import("three")` inside an effect, never as a
// static import. The app is Create React App with no chunking config, main.js
// is already ~816 KB gzipped, and it is served as a PWA to Zebra TC72
// handhelds — a picker must not pay ~150 KB for an admin visualisation they
// will never open. This is the same pattern printAgent.ts uses for jspdf and
// jsbarcode. The dynamic import is what puts three in its own chunk.
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "../../../i18n";
import {
    aisleLength, computeBays, resolvedAt, runsForTier, LayoutModel,
} from "./layoutGeometry";

// Enough of three's surface to type what is used here, so the module can stay
// dynamically imported without `any` leaking through the component.
type ThreeModule = typeof import("three");

/** A drawable spot on the floor: the corridor centre beside a bay, the height
 *  its low racking reaches, and which corridor it belongs to. */
interface PlaceInfo {
    x: number; z: number; y: number; corridor: string;
    /** Which way the racking lies from where the person stands: -1 or +1 along
     *  X. A bay coordinate is the pick FACE and the person stands out in the
     *  corridor, so this is the direction they reach in. */
    faceDx: number;
}

/** One person's last known whereabouts, as /warehouse-layout/live returns it. */
export interface LivePerson {
    uid: number;
    name: string | null;
    /** Seconds since the scan. The display fades with this rather than
     *  pretending a minute-old position is current. */
    age_s: number;
    at: { aisle: string; bay: string; level: string | null; tier: "low" | "high" };
    /** The bay scanned before this one, or null when they have not moved.
     *  Two points are what give a direction; one gives none. */
    from: { aisle: string; bay: string } | null;
    /** The next pending line in their wave — where they are being SENT, as
     *  opposed to where they have been. Pickers only; nothing else on the
     *  floor hands somebody an ordered queue. */
    next?: { aisle: string; bay: string; level: string | null; tier: "low" | "high" } | null;
    /** Routed distance to it, and how long that move should take under the
     *  layout's own cost model. */
    next_m?: number | null;
    next_eta_s?: number | null;
    /** How much of the estimate is spent AT the bay doing the job, before any
     *  walking. A scan happens on arrival; the pick comes after it. */
    next_dwell_s?: number | null;
    eta_source?: "fitted" | "reference" | null;
    /** Whether they are taking stock OFF the racking at this bay or putting it
     *  ON. Null where the action does not say. */
    activity?: "collect" | "place" | null;
}

const WarehouseScene3D = ({ model, walk, tier = "both", people = null }: {
    model: LayoutModel;
    /** Last known positions, polled while the view is open. Drawn into their
     *  own group and refreshed in place — rebuilding the scene every poll
     *  would throw the camera back to its starting angle every few seconds. */
    people?: LivePerson[] | null;
    /** Which tier to draw. The two are different racking — F and G are
     *  shelving below and pallet above — and there are no tunnels up high, so
     *  each is computed from its own runs rather than one being the other
     *  seen from a different height. */
    tier?: "low" | "high" | "both";
    /** The live walk order. Unlike the plan, the 3D view keeps the LEVEL: the
     *  route climbs to the shelf each pick is actually on, which is what shows
     *  an aisle being walked twice for its top shelf. */
    walk?: { aisle: string; bay: string; level: string | null }[] | null;
}) => {
    const { t } = useTranslation();
    const mountRef = useRef<HTMLDivElement | null>(null);
    const [status, setStatus] = useState<"loading" | "ready" | "failed">("loading");
    // The scene is built once per model/tier change; people arrive on a poll.
    // The updater is handed out through a ref so the poll can redraw just the
    // figures, and the latest list is held in a ref so a scene rebuilt for
    // another reason still comes up with everyone on it.
    const drawPeopleRef = useRef<((list: LivePerson[]) => void) | null>(null);
    const peopleRef = useRef<LivePerson[]>(people ?? []);
    peopleRef.current = people ?? [];

    useEffect(() => {
        let disposed = false;
        let cleanup: (() => void) | null = null;

        (async () => {
            let THREE: ThreeModule;
            try {
                THREE = await import("three");
            } catch {
                if (!disposed) setStatus("failed");
                return;
            }
            const mount = mountRef.current;
            if (disposed || !mount) return;

            const width = mount.clientWidth || 800;
            const height = 520;

            const scene = new THREE.Scene();
            scene.background = new THREE.Color(0x0f1115);

            const camera = new THREE.PerspectiveCamera(55, width / height, 0.1, 2000);
            const renderer = new THREE.WebGLRenderer({ antialias: true });
            renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
            renderer.setSize(width, height);
            mount.appendChild(renderer.domElement);

            scene.add(new THREE.AmbientLight(0xffffff, 0.75));
            const key = new THREE.DirectionalLight(0xffffff, 0.9);
            key.position.set(30, 60, 40);
            scene.add(key);

            // Each tier has its own footprint, so each is computed separately.
            const lowBays = computeBays(model, "low").filter(b => !b.unplaced);
            const highBays = computeBays(model, "high").filter(b => !b.unplaced);
            const runOf = new Map<string, { low: number; high: number; shelfGap: number;
                                            board: number }>();
            const highRunOf = new Map<string, { high: number; shelfGap: number; board: number }>();
            const depthOf = new Map<string, number>();
            const sharedOf = new Map<string, boolean>();
            for (const a of model.aisles) {
                depthOf.set(a.aisle, a.rack_depth_m);
                sharedOf.set(a.aisle, a.shared_rack === true);
                for (const r of a.runs) {
                    runOf.set(`${a.aisle}:${r.seq}`, {
                        low: Math.max(0, r.low_shelves),
                        high: Math.max(0, r.high_shelves),
                        shelfGap: r.shelf_gap_height_m,
                        board: r.shelf_height_m,
                    });
                }
                for (const r of runsForTier(a, "high")) {
                    highRunOf.set(`${a.aisle}:${r.seq}`, {
                        high: Math.max(0, r.high_shelves),
                        shelfGap: r.shelf_gap_height_m,
                        board: r.shelf_height_m,
                    });
                }
            }

            // Bounds, so the camera frames the shed rather than starting inside
            // a rack.
            // Framed from BOTH tiers so toggling low/high/both does not move
            // the camera — the shed is the same shed either way.
            let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
            for (const b of [...lowBays, ...highBays]) {
                minX = Math.min(minX, b.x_m!); maxX = Math.max(maxX, b.x_m!);
                // Scene Z is the NEGATION of along_m, so the 3D view reads the
                // same way round as the plan: aisle A bottom-left, and the walk
                // order running away from the camera rather than toward it. A
                // plain z = along_m put A on the right and ran the route
                // backwards, which made the two views hard to compare.
                minZ = Math.min(minZ, -b.along_m!); maxZ = Math.max(maxZ, -b.along_m!);
            }
            const midX = (minX + maxX) / 2;
            const midZ = (minZ + maxZ) / 2;
            const span = Math.max(maxX - minX, maxZ - minZ, 10);

            // One InstancedMesh for every shelf in the warehouse. Pembroke is
            // ~1,600 bays at 3-5 shelves each, so a Mesh per shelf would be
            // ~7,000 draw calls and the view would crawl; instancing makes it
            // one.
            // Shelves are split into four groups so LOW and HIGH read apart at
            // a glance, and so a SHARED rack — one structure carrying a pick
            // face on each side, which is everything from H onward — is
            // distinguishable from an independent one. They are the same
            // geometry; only the colour differs.
            type Box = { x: number; y: number; z: number; d: number; w: number };
            const groups: Record<string, Box[]> = {
                lowOwn: [], highOwn: [], lowShared: [], highShared: [],
            };
            // Each tier is drawn from its OWN bays and its OWN run heights, and
            // stacked at the real height: the high tier sits on top of however
            // tall the low racking is, rather than continuing its shelf pitch.
            // F and G are the case that matters — 0.7m shelving below, 2.0m
            // pallet beams above.
            const push = (b: typeof lowBays[0], y: number, high: boolean, depth: number) => {
                const shared = sharedOf.get(b.aisle) === true;
                const outward = b.side === "b" ? depth / 2 : -depth / 2;
                groups[`${high ? "high" : "low"}${shared ? "Shared" : "Own"}`].push({
                    x: b.x_m! + outward, y, z: -b.along_m!,
                    d: depth, w: Math.max(b.width_m! - 0.05, 0.05),
                });
            };

            const lowTopOf = new Map<string, number>();
            for (const b of lowBays) {
                const run = runOf.get(`${b.aisle}:${b.run_seq}`);
                if (!run) continue;
                const pitch = Math.max(run.shelfGap + run.board, 0.15);
                lowTopOf.set(b.aisle, 0.15 + run.low * pitch);
            }
            if (tier !== "high") {
                for (const b of lowBays) {
                    const depth = depthOf.get(b.aisle) ?? 0.6;
                    const run = runOf.get(`${b.aisle}:${b.run_seq}`)
                        ?? { low: 3, high: 0, shelfGap: 0.4, board: 0.05 };
                    const pitch = Math.max(run.shelfGap + run.board, 0.15);
                    for (let s = 0; s < run.low; s++) push(b, 0.15 + s * pitch, false, depth);
                    lowTopOf.set(b.aisle, 0.15 + run.low * pitch);
                }
            }

            if (tier !== "low") {
                for (const b of highBays) {
                    const depth = depthOf.get(b.aisle) ?? 0.6;
                    const run = highRunOf.get(`${b.aisle}:${b.run_seq}`)
                        ?? { high: 0, shelfGap: 0.4, board: 0.05 };
                    const pitch = Math.max(run.shelfGap + run.board, 0.15);
                    const base = lowTopOf.get(b.aisle) ?? 0.15;
                    for (let s = 0; s < run.high; s++) push(b, base + s * pitch, true, depth);
                }
            }

            // Brand orange for low (the tier the walk order actually governs),
            // a cooler tone for high, and a distinct hue for shared racking.
            const COLOURS: Record<string, number> = {
                lowOwn: 0xff7315, highOwn: 0x7a4a1f,
                lowShared: 0x3aa0ff, highShared: 0x24557f,
            };
            const geo = new THREE.BoxGeometry(1, 0.06, 1);
            const dummy = new THREE.Object3D();
            // Kept so the cleanup can dispose each InstancedMesh explicitly —
            // scene.traverse() releases geometry and materials but not an
            // instanced mesh's own buffers, and a leaked WebGL context makes
            // the canvas go blank on the next mount.
            const shelfMeshes: { dispose(): void; material: { dispose(): void } }[] = [];
            for (const [key, boxes] of Object.entries(groups)) {
                if (boxes.length === 0) continue;
                const mat = new THREE.MeshLambertMaterial({ color: COLOURS[key] });
                const mesh = new THREE.InstancedMesh(geo, mat, boxes.length);
                boxes.forEach((s, i) => {
                    dummy.position.set(s.x, s.y, s.z);
                    dummy.scale.set(s.d, 1, s.w);
                    dummy.updateMatrix();
                    mesh.setMatrixAt(i, dummy.matrix);
                });
                mesh.instanceMatrix.needsUpdate = true;
                scene.add(mesh);
                shelfMeshes.push(mesh);
            }

            // Floor, plus the corridor centre lines so the aisles read as gaps.
            const floor = new THREE.Mesh(
                new THREE.PlaneGeometry(span * 2, span * 2),
                new THREE.MeshLambertMaterial({ color: 0x1b1f27 }),
            );
            floor.rotation.x = -Math.PI / 2;
            floor.position.set(midX, 0, midZ);
            scene.add(floor);

            for (const c of model.corridors) {
                const len = Math.max(
                    ...model.aisles.filter(a => a.corridor_id === c.id).map(a => aisleLength(a)), 1,
                );
                const line = new THREE.Mesh(
                    new THREE.PlaneGeometry(c.width_m, len),
                    new THREE.MeshLambertMaterial({ color: 0x2b3140 }),
                );
                line.rotation.x = -Math.PI / 2;
                line.position.set(c.x_m, 0.01, -len / 2);
                scene.add(line);
            }

            // The live walk order, drawn as a line through the picks in order.
            // Level is kept here — the height of each point is the shelf the
            // pick sits on — so a second pass down an aisle for its top shelf
            // shows up as a climb and a return rather than a flat overlay.
            if (walk && walk.length > 1) {
                // The walk order is a LOW route, so it is drawn against the low
                // bays whatever tier is on screen — the high tier fills the
                // tunnel gaps in, and a route that threaded those would be
                // showing a walk through racking.
                const at = new Map(lowBays.map(b => [`${b.aisle} ${b.bay}`, b]));
                const levelOf = (lv: string | null) => {
                    const m = /(\d+)/.exec(lv ?? "");
                    return m ? Math.max(0, Number(m[1]) - 1) : 0;
                };
                const pts: InstanceType<typeof THREE.Vector3>[] = [];
                for (const st of walk) {
                    const b = at.get(`${st.aisle} ${st.bay}`);
                    if (!b) continue;
                    const run = runOf.get(`${st.aisle}:${b.run_seq}`)
                        ?? { low: 3, high: 0, shelfGap: 0.4, board: 0.05 };
                    // Same pitch the shelves are drawn at, board included —
                    // otherwise the route sags below the levels it points at.
                    const gap = Math.max(run.shelfGap + run.board, 0.15);
                    pts.push(new THREE.Vector3(
                        b.x_m!, 0.25 + levelOf(st.level) * gap, -b.along_m!,
                    ));
                }
                if (pts.length > 1) {
                    const geometry = new THREE.BufferGeometry().setFromPoints(pts);
                    const line = new THREE.Line(
                        geometry,
                        new THREE.LineBasicMaterial({ color: 0xff7315 }),
                    );
                    scene.add(line);
                }
            }

            // Route endpoints, so the start and end of a wave are visible in
            // the same picture as the racking.
            for (const p of model.points) {
                const marker = new THREE.Mesh(
                    new THREE.SphereGeometry(0.6, 16, 12),
                    new THREE.MeshLambertMaterial({ color: 0x38bdf8 }),
                );
                marker.position.set(p.x_m, 0.6, -p.y_m);
                scene.add(marker);
            }

            // -- People on the floor -------------------------------------
            //
            // Nothing tracks anyone between scans, so a figure here means "the
            // last bay this person scanned", not "where this person is". The
            // display is built to say so: it fades as the scan ages and is gone
            // once the scan is older than the window.
            //
            // Two shapes, because the two tiers are different work. Down low
            // somebody is walking; up high they are on an order picker several
            // metres up, and a walking figure drawn at 4m would read as a person
            // floating in mid-air.
            const peopleGroup = new THREE.Group();
            scene.add(peopleGroup);

            const corridorX = new Map(model.corridors.map(c => [c.id, c.x_m]));
            const placeOf = new Map<string, PlaceInfo>();
            for (const b of [...lowBays, ...highBays]) {
                // Stand them in the CORRIDOR, not inside the racking: a bay
                // coordinate is the pick face, which is the edge of the steel.
                const centre = corridorX.get(b.corridor_id);
                const x = centre == null ? b.x_m! : (b.x_m! + centre) / 2;
                placeOf.set(`${b.aisle} ${b.bay}`, {
                    x, z: -b.along_m!, y: lowTopOf.get(b.aisle) ?? 2,
                    corridor: b.corridor_id,
                    faceDx: b.x_m! >= x ? 1 : -1,
                });
            }

            /**
             * How high the pick face is, for a level on an aisle.
             *
             * An order picker is a platform that RIDES the mast, so the height
             * is the job: L6 and L12 in the same bay are a different piece of
             * work and a different climb. Drawing every machine at the top of
             * the low racking threw that away and made an aisle of them look
             * like a row of identical boxes.
             *
             * Low levels divide the low racking's pitch; high ones carry on
             * above whatever the low tier reached, at the HIGH run's pitch —
             * F and G are the case that matters, 0.7m shelving below 2.0m
             * pallet beams.
             */
            const pickHeight = (aisle: string, level: string | null, runSeq: number | null) => {
                const idx = Math.max(0, (parseInt(String(level ?? "").replace(/\D/g, ""), 10) || 1) - 1);
                const low = runOf.get(`${aisle}:${runSeq}`);
                const lowPitch = low ? Math.max(low.shelfGap + low.board, 0.15) : 0.6;
                const lowCount = low?.low ?? 0;
                if (idx < lowCount || lowCount === 0) return 0.15 + idx * lowPitch;
                const high = highRunOf.get(`${aisle}:${runSeq}`);
                const highPitch = high ? Math.max(high.shelfGap + high.board, 0.15) : lowPitch;
                return (lowTopOf.get(aisle) ?? 0.15 + lowCount * lowPitch)
                    + (idx - lowCount) * highPitch;
            };

            const runSeqAt = new Map<string, number | null>();
            for (const b of [...lowBays, ...highBays]) {
                if (!runSeqAt.has(`${b.aisle} ${b.bay}`)) {
                    runSeqAt.set(`${b.aisle} ${b.bay}`, b.run_seq);
                }
            }

            // A name is only legible as a texture; three has no text otherwise.
            const labelFor = (text: string) => {
                const scale = 2;
                const canvas = document.createElement("canvas");
                const probe = canvas.getContext("2d");
                if (!probe) return null;
                probe.font = `${13 * scale}px system-ui, sans-serif`;
                canvas.width = Math.ceil(probe.measureText(text).width) + 16 * scale;
                canvas.height = 22 * scale;
                const c = canvas.getContext("2d");
                if (!c) return null;
                c.font = `${13 * scale}px system-ui, sans-serif`;
                c.fillStyle = "rgba(15,17,21,0.82)";
                c.fillRect(0, 0, canvas.width, canvas.height);
                c.fillStyle = "#f8fafc";
                c.textBaseline = "middle";
                c.fillText(text, 8 * scale, canvas.height / 2);
                const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
                    map: new THREE.CanvasTexture(canvas), transparent: true, depthTest: false,
                }));
                sprite.scale.set((canvas.width / canvas.height) * 0.9, 0.9, 1);
                return sprite;
            };

            const disposeObject = (o: InstanceType<typeof THREE.Object3D>) => {
                o.traverse((n: any) => {
                    n.geometry?.dispose?.();
                    const mats = Array.isArray(n.material) ? n.material : [n.material];
                    for (const m of mats) { m?.map?.dispose?.(); m?.dispose?.(); }
                });
            };

            const disposeGroup = (g: InstanceType<typeof THREE.Group>) => {
                disposeObject(g);
                g.clear();
            };

            // Where a picker walks to reach another corridor: down their own
            // corridor to a crossing, across, then up the far one. A straight
            // line between two corridors would send the figure clean through
            // the racking, which is the one thing the drawing is meant to make
            // obvious.
            /** The path between two bays, as points the figure walks through. */
            const routeOf = (a: PlaceInfo, b: PlaceInfo) => {
                if (a.corridor === b.corridor) return [a, b];
                // Pick the crossing that makes the whole trip shortest.
                let via: number | null = null;
                let best = Infinity;
                for (const c of model.crossings) {
                    const serves = c.connects.length === 0
                        || (c.connects.includes(a.corridor) && c.connects.includes(b.corridor));
                    if (!serves) continue;
                    const at = -resolvedAt(model, c);          // scene Z
                    if (!Number.isFinite(at)) continue;
                    const cost = Math.abs(a.z - at) + Math.abs(a.x - b.x) + Math.abs(b.z - at);
                    if (cost < best) { best = cost; via = at; }
                }
                if (via == null) return [a, b];
                return [a, { ...a, z: via }, { ...b, z: via }, b];
            };

            /** Position along a polyline at fraction t, by arc length. */
            const walkAlong = (pts: { x: number; z: number }[], t: number) => {
                if (pts.length < 2) return pts[0];
                const segs = [];
                let total = 0;
                for (let i = 1; i < pts.length; i++) {
                    const d = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].z - pts[i - 1].z);
                    segs.push(d);
                    total += d;
                }
                if (total <= 0) return pts[0];
                let want = Math.max(0, Math.min(1, t)) * total;
                for (let i = 0; i < segs.length; i++) {
                    if (want <= segs[i] || i === segs.length - 1) {
                        const f = segs[i] > 0 ? want / segs[i] : 0;
                        return {
                            x: pts[i].x + (pts[i + 1].x - pts[i].x) * f,
                            z: pts[i].z + (pts[i + 1].z - pts[i].z) * f,
                        };
                    }
                    want -= segs[i];
                }
                return pts[pts.length - 1];
            };

            // One journey: a polyline to walk, a mast height to ride to, and
            // when it started. A figure can hold two — a short catch-up and
            // then the real walk — which is what keeps it from teleporting.
            type Leg = {
                path: { x: number; z: number }[];
                liftFrom: number;
                liftTo: number;
                /** performance.now() at which the figure was at path[0]. For
                 *  the walk this is in the PAST, because the scan that started
                 *  it is already `age_s` old by the time we hear about it. */
                startedMs: number;
                durationMs: number;
            };

            type Walker = {
                figure: InstanceType<typeof THREE.Group>;
                arrow: InstanceType<typeof THREE.Mesh> | null;
                /** The parts of an order picker that ride the mast, and how far
                 *  above the platform each of them sits, so the whole cage can
                 *  be driven from one number while the truck is moving. */
                riders: { part: InstanceType<typeof THREE.Object3D>; offset: number }[];
                /** The box that is handed between the racking and the person
                 *  while they stand at a bay, and which way it travels. */
                cargo: InstanceType<typeof THREE.Mesh> | null;
                cargoFrom: { x: number; y: number; z: number } | null;
                cargoTo: { x: number; y: number; z: number } | null;
                legs: Leg[];
                /** The destination the current walk leg is for, and when that
                 *  walk began. Kept so a poll that says the same thing does not
                 *  restart the journey. */
                walkTo: string | null;
                walkStartedMs: number;
                /** The bay the person was last known to be at, and what they
                 *  were doing there. Together with walkTo this is the whole of
                 *  what a poll can change; when none of it has, the journey
                 *  already in flight is still the right one. */
                atKey: string;
                activity: string | null;
                /** Where the figure was last drawn, so the next poll can start
                 *  from there rather than snapping. */
                x: number;
                z: number;
                lift: number;
                seen: number;
            };

            // Figures PERSIST between polls, keyed on uid. Rebuilding the group
            // every ten seconds was the jumping: a figure that had walked most
            // of the way to its next bay was disposed and a new one created at
            // the last scanned bay, so it snapped backwards, and a person who
            // beat the estimate snapped forwards. Keeping the mesh means the
            // only thing that changes on a poll is where it is being asked to
            // go, and it can be made to travel there.
            const walkers = new Map<number, Walker>();
            let generation = 0;

            const drawPeople = (list: LivePerson[]) => {
                const nowMs = performance.now();
                generation += 1;
                for (const p of list) {
                    const spot = placeOf.get(`${p.at.aisle} ${p.at.bay}`);
                    if (!spot) continue;                 // a bay the drawing does not have
                    const existing = walkers.get(p.uid);
                    const atKey = `${p.at.aisle} ${p.at.bay}`;
                    const nextKey = p.next ? `${p.next.aisle} ${p.next.bay}` : null;
                    // Fresh scans are solid; from a minute old they fade out.
                    // The fade IS the accuracy: a 20s-old position is worth
                    // acting on, an 85s-old one is a hint.
                    const fade = p.age_s <= 60
                        ? 1
                        : Math.max(0.15, 1 - ((p.age_s - 60) / 30) * 0.85);

                    // NOTHING NEW TO SAY — leave the journey alone.
                    //
                    // This is what stopped figures walking somewhere and then
                    // going back. A poll every ten seconds rebuilt every
                    // journey, and the rebuild starts by catching the figure up
                    // onto the bay the person was last KNOWN to be at. A figure
                    // that had correctly walked ahead toward its next bay was
                    // therefore dragged back to where the walk began — and then
                    // set off again, over and over, because the person had not
                    // scanned yet and so the poll kept saying the same thing.
                    //
                    // A poll only ever changes three things: where they were
                    // last seen, where they are being sent, and what they are
                    // doing there. When none of them has moved, the journey
                    // already in flight is still the right one and the only
                    // thing to update is how faded it should be.
                    if (existing
                        && existing.atKey === atKey
                        && existing.walkTo === nextKey
                        && existing.activity === (p.activity ?? null)) {
                        existing.seen = generation;
                        existing.figure.traverse((o: any) => {
                            const mats = Array.isArray(o.material) ? o.material : [o.material];
                            for (const m of mats) if (m && "opacity" in m) m.opacity = fade;
                        });
                        continue;
                    }
                    const high = p.at.tier === "high";
                    const runSeq = runSeqAt.get(`${p.at.aisle} ${p.at.bay}`) ?? null;
                    // The platform sits at the pick face, not at the top of the
                    // low racking. Floored so the deck never sinks into the
                    // chassis on a level-1 pick.
                    const lift = Math.max(1.2, pickHeight(p.at.aisle, p.at.level, runSeq));
                    const mat = () => new THREE.MeshLambertMaterial({
                        color: high ? 0xfbbf24 : 0x34d399, transparent: true, opacity: fade,
                    });

                    const figure = new THREE.Group();
                    let riders: { part: InstanceType<typeof THREE.Object3D>; offset: number }[] = [];
                    if (high) {
                        // An order picker truck, drawn as the machine actually
                        // is rather than as a box on a stick: a low chassis
                        // with the forks out front at floor level, a twin-rail
                        // mast at the back, and an operator platform that rides
                        // UP that mast with a guard rail round it and the load
                        // deck behind. The operator stands on the platform at
                        // pick height, which is the whole point of the truck
                        // and the reason a walking figure at 4m looked wrong.
                        const CHASSIS_L = 2.2;                 // along the aisle
                        const CHASSIS_W = 1.0;                 // across it
                        const dark = () => new THREE.MeshLambertMaterial({
                            color: 0x1f2937, transparent: true, opacity: fade,
                        });

                        const chassis = new THREE.Mesh(
                            new THREE.BoxGeometry(CHASSIS_W, 0.28, CHASSIS_L), mat());
                        chassis.position.set(0, 0.22, 0);

                        // Forks, at floor level, reaching forward.
                        for (const dx of [-0.28, 0.28]) {
                            const fork = new THREE.Mesh(
                                new THREE.BoxGeometry(0.14, 0.08, 1.2), dark());
                            fork.position.set(dx, 0.06, CHASSIS_L / 2 + 0.5);
                            figure.add(fork);
                        }
                        // Wheels.
                        for (const [wx, wz] of [[-0.42, -0.75], [0.42, -0.75], [0, 0.8]]) {
                            const wheel = new THREE.Mesh(
                                new THREE.CylinderGeometry(0.2, 0.2, 0.16, 12), dark());
                            wheel.rotation.z = Math.PI / 2;
                            wheel.position.set(wx, 0.2, wz);
                            figure.add(wheel);
                        }

                        // Twin mast rails at the back, tall enough for the
                        // TOP of the racking rather than for wherever the
                        // platform happens to be. The mast is a fixed piece of
                        // the machine; sizing it to the current lift made it
                        // grow and shrink as the platform rode up and down.
                        const highRun = highRunOf.get(`${p.at.aisle}:${runSeq}`);
                        const mastTop = Math.max(
                            lift + 0.5,
                            (lowTopOf.get(p.at.aisle) ?? lift)
                                + (highRun?.high ?? 0)
                                    * Math.max(highRun ? highRun.shelfGap + highRun.board : 0.5, 0.15),
                        );
                        for (const dx of [-0.3, 0.3]) {
                            const rail = new THREE.Mesh(
                                new THREE.BoxGeometry(0.12, mastTop, 0.16), dark());
                            rail.position.set(dx, mastTop / 2, -CHASSIS_L / 2 + 0.2);
                            figure.add(rail);
                        }

                        // The platform, at pick height.
                        const deck = new THREE.Mesh(
                            new THREE.BoxGeometry(CHASSIS_W, 0.1, 1.3), mat());
                        deck.position.set(0, lift, 0.2);
                        // Guard rail: three sides, open toward the racking.
                        const railMat = dark;
                        const rails: [number, number, number, number][] = [
                            [0, 0.55, -0.45, 0],         // back
                            [-0.47, 0.55, 0.2, 1],       // left
                            [0.47, 0.55, 0.2, 1],        // right
                        ];
                        const railParts: [InstanceType<typeof THREE.Mesh>, number][] = [];
                        for (const [rx, dy, rz, along] of rails) {
                            const bar = new THREE.Mesh(
                                along
                                    ? new THREE.BoxGeometry(0.06, 0.08, 1.3)
                                    : new THREE.BoxGeometry(CHASSIS_W, 0.08, 0.06),
                                railMat());
                            bar.position.set(rx, lift + dy, rz);
                            figure.add(bar);
                            railParts.push([bar, dy]);
                        }
                        // Load deck behind the operator — what the picks go on.
                        const load = new THREE.Mesh(
                            new THREE.BoxGeometry(0.85, 0.35, 0.7), dark());
                        load.position.set(0, lift + 0.28, -0.25);

                        const rider = new THREE.Mesh(
                            new THREE.CapsuleGeometry(0.17, 0.5, 4, 8), mat());
                        rider.position.set(0, lift + 0.5, 0.55);
                        const riderHead = new THREE.Mesh(
                            new THREE.SphereGeometry(0.16, 12, 10), mat());
                        riderHead.position.set(0, lift + 1.0, 0.55);

                        figure.add(chassis, deck, load, rider, riderHead);
                        // Everything that rides the mast, with its height above
                        // the platform, so the whole cage can be driven from one
                        // number as the truck travels.
                        riders = [
                            { part: deck, offset: 0 },
                            { part: load, offset: 0.28 },
                            { part: rider, offset: 0.5 },
                            { part: riderHead, offset: 1.0 },
                            ...railParts.map(([bar, dy]) => ({ part: bar, offset: dy })),
                        ];
                    } else {
                        const body = new THREE.Mesh(
                            new THREE.CapsuleGeometry(0.2, 0.75, 4, 8), mat());
                        body.position.y = 0.75;
                        const head = new THREE.Mesh(new THREE.SphereGeometry(0.19, 12, 10), mat());
                        head.position.y = 1.45;
                        figure.add(body, head);
                    }

                    // Where they are HEADING beats where they have been. A
                    // picker with a wave has a next line, and that is a real
                    // prediction; the bay before this one is only a heading
                    // inferred from two points, and is the fallback.
                    const dest = p.next ? placeOf.get(`${p.next.aisle} ${p.next.bay}`) : null;
                    const prev = p.from ? placeOf.get(`${p.from.aisle} ${p.from.bay}`) : null;
                    const toward = dest ?? null;
                    const heading = toward
                        ? { dx: toward.x - spot.x, dz: toward.z - spot.z }
                        : prev
                            ? { dx: spot.x - prev.x, dz: spot.z - prev.z }
                            : null;

                    let arrow: InstanceType<typeof THREE.Mesh> | null = null;
                    if (heading && Math.hypot(heading.dx, heading.dz) > 1e-6) {
                        arrow = new THREE.Mesh(
                            new THREE.ConeGeometry(0.22, 0.6, 10),
                            new THREE.MeshLambertMaterial({
                                color: 0xff7315, transparent: true, opacity: fade,
                            }),
                        );
                        // The cone points up +Y by default; lay it flat and
                        // swing it round to the heading.
                        arrow.rotation.z = -Math.PI / 2;
                        arrow.rotation.y = Math.atan2(-heading.dz, heading.dx);
                        arrow.position.set(0, high ? lift + 1.3 : 1.95, 0);
                        figure.add(arrow);
                    }

                    const label = labelFor(p.name ?? `#${p.uid}`);
                    if (label) {
                        label.position.set(0, high ? lift + 2.0 : 2.5, 0);
                        label.material.opacity = fade;
                        figure.add(label);
                    }
                    // The name and the heading sit above the operator, so on a
                    // machine they ride the platform rather than hanging at
                    // whatever height it happened to start at.
                    if (high) {
                        if (label) riders.push({ part: label, offset: 2.0 });
                        if (arrow) riders.push({ part: arrow, offset: 1.3 });
                    }

                    // ── The journey ──────────────────────────────────────
                    //
                    // Up to two legs. The first is a CATCH-UP: the figure is
                    // wherever it had animated to, and the person is at the bay
                    // they last scanned, and those are rarely the same — a
                    // picker who beat the estimate is ahead of their icon, one
                    // who was slower is behind it. Snapping the mesh onto the
                    // truth was the jumping. Walking it there over three
                    // quarters of a second is still honest — the figure is
                    // never more than that stale — and it reads as movement.
                    //
                    // The second leg is the walk to the next bay, clocked from
                    // when the scan actually happened.
                    const from = existing
                        ? { x: existing.x, z: existing.z }
                        : { x: spot.x, z: spot.z };
                    const liftNow = existing ? existing.lift : lift;
                    const here = { x: spot.x, z: spot.z };
                    const legs: Leg[] = [];

                    // The scan is the clock. Everything after it is measured
                    // from when it actually happened, so a poll never restarts
                    // anything — it only revises where the figure is going.
                    const scanMs = nowMs - p.age_s * 1000;
                    let cursor = scanMs;

                    // Leg 1, only when needed: catch up onto the bay they last
                    // scanned. The figure is wherever it animated to and the
                    // person is at the bay — a picker who beat the estimate is
                    // ahead of their icon, one who was slower is behind it, and
                    // snapping the mesh onto the truth was the jumping. Walking
                    // it there over three quarters of a second is still honest:
                    // the figure is never staler than that.
                    const adrift = Math.hypot(from.x - here.x, from.z - here.z) > 0.15
                        || Math.abs(liftNow - lift) > 0.15;
                    if (adrift) {
                        legs.push({
                            path: [from, here],
                            liftFrom: liftNow, liftTo: lift,
                            startedMs: nowMs, durationMs: 750,
                        });
                        cursor = nowMs + 750;
                    }

                    const walkTo = nextKey;
                    let walkStartedMs = existing?.walkStartedMs ?? nowMs;

                    if (toward && p.next_eta_s && p.next_eta_s > 0) {
                        const toRun = runSeqAt.get(`${p.next!.aisle} ${p.next!.bay}`) ?? null;
                        const dwellMs = Math.max(0, (p.next_dwell_s ?? 0) * 1000);
                        const walkMs = Math.max(200, p.next_eta_s * 1000 - dwellMs);

                        // WHEN the walk started is the difference between a
                        // figure that moves and one that teleports.
                        //
                        // Not the scan. A picker with three lines in one bay
                        // scans on arrival and then works there for a minute,
                        // and clocking the walk from that scan meant the
                        // estimate had long expired before they took a step —
                        // so the figure was already parked at the next bay and
                        // jumped to the one after it. The destination only
                        // appears once the current bay is finished, so THAT is
                        // the moment they set off, and a later poll saying the
                        // same thing must not restart it.
                        if (!existing || existing.walkTo !== walkTo) walkStartedMs = nowMs;
                        cursor = Math.max(cursor, walkStartedMs);

                        // Leg 2: stand at the bay and do the job. A zero-length
                        // path, which is exactly what standing still is, and
                        // what the box animation plays over.
                        if (dwellMs > 0) {
                            legs.push({
                                path: [here, here],
                                liftFrom: lift, liftTo: lift,
                                startedMs: cursor, durationMs: dwellMs,
                            });
                            cursor += dwellMs;
                        }

                        // Leg 3: the walk.
                        legs.push({
                            path: routeOf(spot, toward),
                            liftFrom: lift,
                            // A machine on its way to a level-2 pick comes DOWN
                            // as it drives, and one heading for level 12 goes
                            // up — whichever tier the next bay is on. The mast
                            // and the wheels run off the same clock, so the
                            // climb happens DURING the drive rather than
                            // instantly on arrival.
                            liftTo: Math.max(1.2, pickHeight(
                                p.next!.aisle, p.next!.level, toRun)),
                            startedMs: cursor, durationMs: walkMs,
                        });
                    }

                    // ── Standing at a bay, doing the job ─────────────────
                    //
                    // Most of a shift is spent stationary, and "stationary" is
                    // not the same as "idle": somebody at a bay is reaching in
                    // and taking a box out, or reaching in and putting one
                    // back. Which of the two is what the action says, and it is
                    // the only thing the map can tell you about the work
                    // itself. A box that travels the wrong way would be a
                    // picture of the wrong job, so an unknown action animates
                    // nothing at all.
                    let cargo: InstanceType<typeof THREE.Mesh> | null = null;
                    let cargoFrom: { x: number; y: number; z: number } | null = null;
                    let cargoTo: { x: number; y: number; z: number } | null = null;
                    if (p.activity) {
                        const depth = depthOf.get(p.at.aisle) ?? 0.6;
                        // The shelf side of the bay, and the person's hands.
                        const shelf = {
                            x: spot.faceDx * (depth * 0.5 + 0.35),
                            y: high ? lift + 0.55 : Math.max(0.9, lift),
                            z: high ? 0.5 : 0,
                        };
                        const hands = { x: 0, y: shelf.y - 0.1, z: high ? 0.35 : 0 };
                        cargo = new THREE.Mesh(
                            new THREE.BoxGeometry(0.3, 0.24, 0.3),
                            new THREE.MeshLambertMaterial({
                                color: 0xff7315, transparent: true, opacity: fade,
                            }),
                        );
                        cargoFrom = p.activity === "collect" ? shelf : hands;
                        cargoTo = p.activity === "collect" ? hands : shelf;
                        cargo.position.set(cargoFrom.x, cargoFrom.y, cargoFrom.z);
                        figure.add(cargo);
                    }

                    if (existing) {
                        peopleGroup.remove(existing.figure);
                        disposeObject(existing.figure);
                    }
                    figure.position.set(from.x, 0, from.z);
                    // The mesh was built at the scanned bay's pick height, but
                    // the figure is starting from wherever it had got to. Put
                    // the platform there before the first frame is drawn.
                    for (const r of riders) r.part.position.y = liftNow + r.offset;
                    peopleGroup.add(figure);
                    walkers.set(p.uid, {
                        figure, arrow, riders, cargo, cargoFrom, cargoTo, legs,
                        walkTo, walkStartedMs, atKey, activity: p.activity ?? null,
                        x: from.x, z: from.z, lift: liftNow, seen: generation,
                    });
                }

                // Anyone who has dropped out of the window goes with them.
                for (const [uid, w] of walkers) {
                    if (w.seen === generation) continue;
                    peopleGroup.remove(w.figure);
                    disposeObject(w.figure);
                    walkers.delete(uid);
                }
            };
            drawPeopleRef.current = drawPeople;
            drawPeople(peopleRef.current);

            // Orbit without pulling in OrbitControls — one more module for a
            // drag-to-rotate that is a dozen lines here.
            // Start on the +Z side looking back down the shed, so the opening
            // view matches the plan: A on the left, the route running away.
            // Nudged off dead-centre for a three-quarter view rather than a
            // flat elevation.
            let theta = Math.PI / 2 - 0.35;
            let phi = 0.85;
            let radius = span * 1.4;
            const applyCamera = () => {
                camera.position.set(
                    midX + radius * Math.sin(phi) * Math.cos(theta),
                    radius * Math.cos(phi),
                    midZ + radius * Math.sin(phi) * Math.sin(theta),
                );
                camera.lookAt(midX, 0, midZ);
            };
            applyCamera();

            let dragging = false;
            let lastX = 0, lastY = 0;
            const el = renderer.domElement;
            const onDown = (e: PointerEvent) => {
                dragging = true; lastX = e.clientX; lastY = e.clientY;
                el.setPointerCapture(e.pointerId);
            };
            const onMove = (e: PointerEvent) => {
                if (!dragging) return;
                theta -= (e.clientX - lastX) * 0.008;
                phi = Math.min(Math.PI / 2.05, Math.max(0.15, phi - (e.clientY - lastY) * 0.006));
                lastX = e.clientX; lastY = e.clientY;
                applyCamera();
            };
            const onUp = (e: PointerEvent) => {
                dragging = false;
                el.releasePointerCapture?.(e.pointerId);
            };
            const onWheel = (e: WheelEvent) => {
                e.preventDefault();
                radius = Math.min(span * 6, Math.max(span * 0.2, radius * (e.deltaY > 0 ? 1.12 : 1 / 1.12)));
                applyCamera();
            };
            el.addEventListener("pointerdown", onDown);
            el.addEventListener("pointermove", onMove);
            el.addEventListener("pointerup", onUp);
            el.addEventListener("wheel", onWheel, { passive: false });

            let frame = 0;
            const tick = () => {
                frame = requestAnimationFrame(tick);
                // Move anyone who is on their way somewhere, and give anyone
                // who has arrived something to do with their hands.
                if (walkers.size > 0) {
                    const now = performance.now();
                    for (const w of walkers.values()) {
                        // The active leg: the first that has not finished. A
                        // figure holds at most two — a short catch-up onto its
                        // last known bay, then the walk to the next one.
                        let leg: typeof w.legs[0] | null = null;
                        let t = 1;
                        for (const l of w.legs) {
                            const lt = (now - l.startedMs) / l.durationMs;
                            if (lt < 1) { leg = l; t = Math.max(0, lt); break; }
                            leg = l; t = 1;
                        }

                        if (leg) {
                            const at = walkAlong(leg.path, t);
                            // Turn the arrow through the corner. A cross-corridor
                            // route bends twice — down your own corridor, across,
                            // up the far one — so an arrow fixed on the straight
                            // line to the destination points into the racking for
                            // most of the walk.
                            if (w.arrow) {
                                const ahead = walkAlong(leg.path, Math.min(1, t + 0.02));
                                const dx = ahead.x - at.x;
                                const dz = ahead.z - at.z;
                                if (Math.hypot(dx, dz) > 1e-6) {
                                    w.arrow.rotation.y = Math.atan2(-dz, dx);
                                }
                            }
                            // The mast rides on the SAME clock as the wheels,
                            // so the machine climbs while it drives rather than
                            // arriving and then going up.
                            const y = leg.liftFrom + (leg.liftTo - leg.liftFrom) * t;
                            if (w.riders.length > 0) {
                                for (const r of w.riders) r.part.position.y = y + r.offset;
                            }
                            w.x = at.x;
                            w.z = at.z;
                            w.lift = y;
                            w.figure.position.x = at.x;
                            w.figure.position.z = at.z;
                        }

                        // Standing at a bay: the box goes in or comes out, on a
                        // slow loop. Eased so it settles at each end rather than
                        // ping-ponging, which reads as a hand rather than a
                        // metronome.
                        //
                        // Only while STATIONARY. A dwell leg is a zero-length
                        // path — standing still, which is exactly when the work
                        // is being done — and a box being handed to a shelf
                        // three bays away while the picker walks past it would
                        // be a picture of something that never happens.
                        const still = !leg
                            || (leg.path.length > 0
                                && Math.hypot(
                                    leg.path[leg.path.length - 1].x - leg.path[0].x,
                                    leg.path[leg.path.length - 1].z - leg.path[0].z,
                                ) < 0.05);
                        if (w.cargo) w.cargo.visible = still;
                        if (still && w.cargo && w.cargoFrom && w.cargoTo) {
                            const phase = ((now % 2600) / 2600) * 2;
                            const raw = phase < 1 ? phase : 2 - phase;
                            const e = raw * raw * (3 - 2 * raw);
                            w.cargo.position.set(
                                w.cargoFrom.x + (w.cargoTo.x - w.cargoFrom.x) * e,
                                w.cargoFrom.y + (w.cargoTo.y - w.cargoFrom.y) * e
                                    + Math.sin(e * Math.PI) * 0.12,
                                w.cargoFrom.z + (w.cargoTo.z - w.cargoFrom.z) * e,
                            );
                        }
                    }
                }
                renderer.render(scene, camera);
            };
            tick();
            setStatus("ready");

            // WebGL contexts are a limited resource and are NOT released by
            // dropping the React tree — without forceContextLoss the browser
            // starts evicting older contexts after a handful of remounts and
            // the canvas silently goes blank.
            cleanup = () => {
                drawPeopleRef.current = null;
                disposeGroup(peopleGroup);
                walkers.clear();
                cancelAnimationFrame(frame);
                el.removeEventListener("pointerdown", onDown);
                el.removeEventListener("pointermove", onMove);
                el.removeEventListener("pointerup", onUp);
                el.removeEventListener("wheel", onWheel);
                geo.dispose();
                for (const m of shelfMeshes) { m.material.dispose(); m.dispose(); }
                scene.traverse((o) => {
                    const m = o as unknown as { geometry?: { dispose(): void };
                                                material?: { dispose(): void } };
                    m.geometry?.dispose?.();
                    m.material?.dispose?.();
                });
                renderer.forceContextLoss();
                renderer.dispose();
                if (el.parentNode) el.parentNode.removeChild(el);
            };
        })();

        return () => { disposed = true; cleanup?.(); };
    }, [model, walk, tier]);

    // People arrive on a poll; the scene does not change. Redrawing only
    // the figures is what stops the camera being thrown back to its opening
    // angle every few seconds.
    useEffect(() => { drawPeopleRef.current?.(people ?? []); }, [people]);

    return (
        <div className="wlayout-3d">
            {status === "loading" && <div className="admin-settings-empty">{t("wlayout_3d_loading")}</div>}
            {status === "failed" && <div className="admin-settings-empty">{t("wlayout_3d_failed")}</div>}
            <div ref={mountRef} className="wlayout-3d-mount" />
            <p className="wlayout-hint">{t("wlayout_3d_hint")}</p>
        </div>
    );
};

export default WarehouseScene3D;
